import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Pending,
  Cancel,
  HourglassEmpty,
  UploadFile,
  Add
} from '@mui/icons-material';
import { financeAPI } from '../services/api';

const Finance = () => {
  const navigate = useNavigate();
  const [financeRequests, setFinanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [applyDialog, setApplyDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [requestedAmount, setRequestedAmount] = useState('');
  const [gstDocument, setGstDocument] = useState(null);
  const [panDocument, setPanDocument] = useState(null);
  const [bankStatement, setBankStatement] = useState(null);

  useEffect(() => {
    fetchFinanceRequests();
  }, []);

  const fetchFinanceRequests = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await financeAPI.getFinanceRequests();
      if (response.data.success) {
        setFinanceRequests(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch finance requests');
      console.error('Error fetching finance requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleApplyClick = () => {
    setRequestedAmount('');
    setGstDocument(null);
    setPanDocument(null);
    setBankStatement(null);
    setApplyDialog(true);
  };

  const handleSubmit = async () => {
    if (!gstDocument && !panDocument && !bankStatement) {
      setError('Please upload at least one document (GST, PAN, or Bank Statement)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();

      if (requestedAmount) {
        formData.append('requested_amount', requestedAmount);
      }

      if (gstDocument) {
        formData.append('gst_document', gstDocument);
      }

      if (panDocument) {
        formData.append('pan_document', panDocument);
      }

      if (bankStatement) {
        formData.append('bank_statement', bankStatement);
      }

      await financeAPI.submitFinanceRequest(formData);
      setSuccess('Finance request submitted successfully!');
      setApplyDialog(false);
      fetchFinanceRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit finance request');
      console.error('Error submitting finance request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'under_review':
        return <HourglassEmpty sx={{ color: 'info.main' }} />;
      case 'pending':
        return <Pending sx={{ color: 'warning.main' }} />;
      case 'rejected':
        return <Cancel sx={{ color: 'error.main' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'under_review':
        return 'info';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            Loha Mart
          </Typography>
          <Button color="inherit" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate('/products')}>
            Products
          </Button>
          <Button color="inherit" onClick={() => navigate('/quotes')}>
            My Quotes
          </Button>
          <Button color="inherit" onClick={() => navigate('/orders')}>
            My Orders
          </Button>
          <Button color="inherit" onClick={() => navigate('/finance')}>
            Finance
          </Button>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        <Box py={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4">
              Finance Requests
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleApplyClick}
            >
              Apply for Finance
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : financeRequests.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No finance requests yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Apply for finance to get funding for your business purchases
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleApplyClick}
              >
                Apply for Finance
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {financeRequests.map((request) => (
                <Grid item xs={12} key={request.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Finance Request #{request.id}
                          </Typography>
                          <Chip
                            icon={getStatusIcon(request.status)}
                            label={request.status.toUpperCase().replace('_', ' ')}
                            size="small"
                            color={getStatusColor(request.status)}
                          />
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" color="text.secondary" display="block">
                            Submitted on
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatDate(request.created_at)}
                          </Typography>
                          {request.requested_amount && (
                            <>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                Requested Amount
                              </Typography>
                              <Typography variant="h6" color="primary">
                                ₹{parseFloat(request.requested_amount).toLocaleString()}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" gutterBottom>
                        Uploaded Documents
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            GST Document
                          </Typography>
                          <Typography variant="body2">
                            {request.gst_document ? (
                              <Button
                                size="small"
                                component="a"
                                href={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${request.gst_document}`}
                                target="_blank"
                              >
                                View Document
                              </Button>
                            ) : (
                              'Not uploaded'
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            PAN Document
                          </Typography>
                          <Typography variant="body2">
                            {request.pan_document ? (
                              <Button
                                size="small"
                                component="a"
                                href={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${request.pan_document}`}
                                target="_blank"
                              >
                                View Document
                              </Button>
                            ) : (
                              'Not uploaded'
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">
                            Bank Statement
                          </Typography>
                          <Typography variant="body2">
                            {request.bank_statement ? (
                              <Button
                                size="small"
                                component="a"
                                href={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${request.bank_statement}`}
                                target="_blank"
                              >
                                View Document
                              </Button>
                            ) : (
                              'Not uploaded'
                            )}
                          </Typography>
                        </Grid>
                      </Grid>

                      {request.admin_remarks && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom>
                            Admin Remarks
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.admin_remarks}
                          </Typography>
                        </>
                      )}

                      {request.reviewed_at && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary">
                            Reviewed on {formatDate(request.reviewed_at)}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>

      {/* Apply for Finance Dialog */}
      <Dialog open={applyDialog} onClose={() => !submitting && setApplyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Apply for Finance
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Upload your business documents to apply for financing. At least one document is required.
          </Typography>

          <TextField
            label="Requested Amount (Optional)"
            type="number"
            fullWidth
            value={requestedAmount}
            onChange={(e) => setRequestedAmount(e.target.value)}
            margin="normal"
            placeholder="Enter amount if known"
            inputProps={{ min: 0, step: 1000 }}
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              GST Document
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadFile />}
              sx={{ mb: 1 }}
            >
              {gstDocument ? gstDocument.name : 'Upload GST Document'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setGstDocument(e.target.files[0])}
              />
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              PAN Document
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadFile />}
              sx={{ mb: 1 }}
            >
              {panDocument ? panDocument.name : 'Upload PAN Document'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setPanDocument(e.target.files[0])}
              />
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Bank Statement
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadFile />}
              sx={{ mb: 1 }}
            >
              {bankStatement ? bankStatement.name : 'Upload Bank Statement'}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setBankStatement(e.target.files[0])}
              />
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            Accepted formats: PDF, JPG, PNG (Max 10MB per file)
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || (!gstDocument && !panDocument && !bankStatement)}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Finance;
